/**
 * Monta payload de URA para o softphone a partir da conexão do CRM.
 * (VOZ cloud muitas vezes não alcança localhost — o pai injeta isso.)
 */
export function parseChannelVoiceIvrOptions(raw) {
  let arr = [];
  if (Array.isArray(raw)) arr = raw;
  else if (typeof raw === "string" && raw.trim()) {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) arr = p;
    } catch {
      return [];
    }
  }
  return arr
    .map((o) => ({
      digit: String(o?.digit ?? "").trim(),
      label: String(o?.label || "").trim().slice(0, 80),
      queueId: Number(o?.queueId),
    }))
    .filter((o) => /^[0-9*#]$/.test(o.digit) && o.queueId > 0);
}

export function buildChannelVoiceIvrForSoftphone(channel, backendUrl = "") {
  if (!channel) return null;
  if (!channel.voiceIvrEnabled) return null;

  const options = parseChannelVoiceIvrOptions(channel.voiceIvrOptions);
  const timeoutSec = Math.min(
    60,
    Math.max(5, Number(channel.voiceIvrTimeoutSec) || 15)
  );
  const fallbackQueueId = channel.voiceIvrFallbackQueueId
    ? Number(channel.voiceIvrFallbackQueueId)
    : null;

  const modeRaw = String(channel.voiceIvrInputMode || "auto").toLowerCase();
  let inputMode = modeRaw === "dtmf" || modeRaw === "speech" ? modeRaw : null;
  if (!inputMode) {
    // auto → voz (STT); teclado se voiceIvrInputMode=dtmf
    inputMode = "speech";
  }

  if (!options.length && !channel.voiceIvrWelcomeAudio && !fallbackQueueId) {
    return null;
  }

  let welcomeAudioUrl = "";
  const rel = String(channel.voiceIvrWelcomeAudio || "").trim();
  if (rel) {
    if (/^https?:\/\//i.test(rel)) welcomeAudioUrl = rel;
    else {
      const base = String(backendUrl || "").replace(/\/$/, "");
      const path = rel.replace(/^\/+/, "").replace(/^public\//, "");
      if (base) welcomeAudioUrl = `${base}/public/${path}`;
    }
  }

  return {
    enabled: true,
    phase: "pending",
    claimClientId: null,
    timeoutSec,
    hasWelcomeAudio: Boolean(welcomeAudioUrl || channel.voiceIvrWelcomeAudio),
    welcomeAudioUrl: welcomeAudioUrl || null,
    options,
    fallbackQueueId,
    inputMode,
    speechHint: channel.voiceIvrSpeechHint || null,
    whatsappId: channel.id ? Number(channel.id) : null,
    queueRingTimeoutSec: Math.min(
      120,
      Math.max(10, Number(channel.voiceQueueRingTimeoutSec) || 30)
    ),
    queueMaxWaitSec: Math.min(
      600,
      Math.max(15, Number(channel.voiceQueueMaxWaitSec) || 120)
    ),
    queueHoldAudioUrl: publicAudioUrl(
      channel.voiceQueueHoldAudio,
      backendUrl
    ),
    queueGoodbyeAudioUrl: publicAudioUrl(
      channel.voiceQueueGoodbyeAudio,
      backendUrl
    ),
    hasQueueHoldAudio: Boolean(channel.voiceQueueHoldAudio),
    hasQueueGoodbyeAudio: Boolean(channel.voiceQueueGoodbyeAudio),
  };
}

function publicAudioUrl(rel, backendUrl) {
  const p = String(rel || "").trim();
  if (!p) return null;
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(backendUrl || "").replace(/\/$/, "");
  const path = p.replace(/^\/+/, "").replace(/^public\//, "");
  if (!base) return null;
  return `${base}/public/${path}`;
}
